import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetAllAccountsQuery,
  useSetAccountArchivedMutation,
} from "store/queries/account";
import { onAdminAccountChange } from "store/slices/user";
import styled from "styled-components";
import { IProvider, SpecialtyList } from "types/Provider/Provider";

import { LoadingSpinner } from "../../../../components/LoadingSpinner";
import { useUserWithAccount } from "../../../../context/UserAccountProvider";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const ArchivedToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
`;

export function HospitalsTable() {
  // ToastContext defaults to null, so read it defensively rather than destructuring.
  const toastElement = useContext(ToastContext)?.current;
  const { accountRefetch } = useUserWithAccount();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showArchived, setShowArchived] = useState(false);

  // CLE-2670: passing `null` when archived rows aren't wanted deliberately reuses the cache entry
  // UserAccountProvider already populated, so the default view needs no extra request.
  const {
    data: accounts,
    isLoading,
    refetch,
  } = useGetAllAccountsQuery(showArchived ? { includeArchived: true } : null);

  const [setAccountArchived, { isLoading: isSaving }] =
    useSetAccountArchivedMutation();

  const parsedAccounts = useMemo(() => {
    if (!accounts?.data) return [];

    return accounts.data.map((aAccount) => {
      const theProvider = (
        (aAccount.providers as unknown as IProvider[]) ?? []
      ).find((aProvider) => {
        return aProvider.specialty === SpecialtyList.FACILITY;
      });

      return {
        id: aAccount.id,
        hospitalName: aAccount.name,
        rcm: theProvider?.rcm,
        reportType: aAccount.reconciliationConfig?.type,
        isArchived: aAccount.accountStatus === "Inactive",
        // paymentContact is null on older accounts that never finished onboarding — most of
        // which are exactly the ones sitting in the archived list.
        pointOfContactName: theProvider?.paymentContact?.contactName,
        pointOfContactPhone: theProvider?.paymentContact?.contactPhone,
        pointOfContactEmail: theProvider?.paymentContact?.contactEmail,
      };
    });
  }, [accounts]);

  function handleOnViewToViewHospital(accountId: any) {
    dispatch(onAdminAccountChange({ currentAccount: accountId }));
    navigate(`/hospitals/view/${accountId}`);
  }

  function saveArchived(row: {
    id: string;
    hospitalName: string;
    isArchived: boolean;
  }) {
    setAccountArchived({ id: row.id, archived: !row.isArchived })
      .unwrap()
      .then(() => {
        // Refresh both this table and the list behind the account switcher.
        refetch();
        accountRefetch();
        toastElement?.show({
          severity: "success",
          summary: row.isArchived ? "Hospital restored" : "Hospital archived",
          detail: row.isArchived
            ? `${row.hospitalName} is back in the hospitals list.`
            : `${row.hospitalName} is hidden. Turn on "Show archived" to bring it back.`,
        });
      })
      .catch((error) =>
        toastElement?.show({
          severity: "error",
          summary: "Error",
          detail: `Message: ${error?.data?.message ?? error}`,
        })
      );
  }

  function handleOnClickToArchive(row: {
    id: string;
    hospitalName: string;
    isArchived: boolean;
  }) {
    confirmDialog({
      header: row.isArchived ? "Restore hospital" : "Archive hospital",
      icon: "pi pi-exclamation-triangle",
      message: row.isArchived
        ? `Restore "${row.hospitalName}"? It goes back into the hospitals list, the account switcher and the reports.`
        : `Archive "${row.hospitalName}"? It is hidden from the hospitals list and the account switcher, left out of the weekly reports, and its Direct Pay links stop taking payments. Its estimates, payments and history are kept, and you can bring it back from "Show archived".`,
      acceptLabel: row.isArchived ? "Restore" : "Archive",
      rejectLabel: "Cancel",
      acceptClassName: row.isArchived ? undefined : "p-button-danger",
      // Without a width the dialog stretches to fit the message on one line.
      style: { width: "30rem", maxWidth: "90vw" },
      accept: () => saveArchived(row),
    });
  }

  function accountAction(row: {
    id: string;
    hospitalName: string;
    isArchived: boolean;
  }) {
    return (
      // The table navigates on row click, so the actions have to keep their click to themselves.
      <Row onClick={(event) => event.stopPropagation()}>
        <Button
          className="p-button-link"
          onClick={() => navigate(`/hospitals/edit/${row.id}`)}
        >
          <p className="small-text">Edit</p>
        </Button>
        <Button
          className="p-button-link"
          disabled={isSaving}
          onClick={() => handleOnClickToArchive(row)}
        >
          <p className="small-text">{row.isArchived ? "Restore" : "Archive"}</p>
        </Button>
      </Row>
    );
  }

  function hospitalName(row: { hospitalName: string; isArchived: boolean }) {
    return (
      <Row style={{ alignItems: "center", gap: "0.5rem" }}>
        <span style={{ color: "#4338ca" }}>{row.hospitalName}</span>
        {row.isArchived && <Tag severity="warning" value="Archived" />}
      </Row>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <ArchivedToggle>
        <InputSwitch
          inputId="showArchivedHospitals"
          checked={showArchived}
          onChange={(event) => setShowArchived(!!event.value)}
        />
        <label htmlFor="showArchivedHospitals">Show archived</label>
      </ArchivedToggle>
      <DataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={parsedAccounts}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        emptyMessage="No hospitals to show."
        selectionMode="single"
        onRowClick={({ data }) => handleOnViewToViewHospital(data.id)}
      >
        <Column
          filter
          filterMatchMode="contains"
          showFilterMenu={false}
          field="hospitalName"
          body={(row) => hospitalName(row)}
          style={{ minWidth: "320px" }}
          header="Hospital Name"
          filterPlaceholder="Search by hospital name"
        />
        <Column
          filter
          filterMatchMode="contains"
          showFilterMenu={false}
          field="rcm"
          header="RCM"
          style={{ minWidth: "280px", textTransform: "capitalize" }}
          filterPlaceholder="Search by RCM"
        />
        <Column
          filter
          filterMatchMode="contains"
          showFilterMenu={false}
          field="reportType"
          style={{ minWidth: "280px" }}
          header="Search by type"
          filterPlaceholder="Search by type"
        />
        <Column
          style={{ maxWidth: "160px" }}
          field="viewEditAccount"
          body={(row) => accountAction(row)}
        />
      </DataTable>
    </>
  );
}
